import React, { useState, useEffect } from 'react';
import { storeAPI, propertyAPI, userAPI } from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Store, Search, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getStatusColor, STORE_TYPES } from '../lib/utils';

export const StoresPage = () => {
  const [stores, setStores] = useState([]);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    property_id: '',
    store_type: '',
    incharge_id: '',
    notes: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, propertiesRes, usersRes] = await Promise.all([
        storeAPI.getAll(),
        propertyAPI.getAll({ status: 'active' }),
        userAPI.getAll({ status: 'active' })
      ]);
      setStores(storesRes.data);
      setProperties(propertiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (store = null) => {
    if (store) {
      setSelectedStore(store);
      setFormData({
        name: store.name || '',
        code: store.code || '',
        property_id: store.property_id || '',
        store_type: store.store_type || '',
        incharge_id: store.incharge_id || '',
        notes: store.notes || '',
        status: store.status || 'active'
      });
    } else {
      setSelectedStore(null);
      setFormData({
        name: '',
        code: '',
        property_id: properties[0]?.id || '',
        store_type: '',
        incharge_id: '',
        notes: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStore(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (selectedStore) {
        await storeAPI.update(selectedStore.id, formData);
        toast.success('Store updated successfully');
      } else {
        await storeAPI.create(formData);
        toast.success('Store created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save store');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await storeAPI.delete(selectedStore.id);
      toast.success('Store deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedStore(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete store');
    }
  };

  const filteredStores = stores.filter(s => {
    const matchesSearch = s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProperty = filterProperty === 'all' || s.property_id === filterProperty;
    return matchesSearch && matchesProperty;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stores</h1>
          <p className="text-muted-foreground mt-1">Manage stores and stock locations</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full" data-testid="add-store-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Store
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-stores-input"
              />
            </div>
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="w-[180px]" data-testid="filter-property-select">
                <SelectValue placeholder="Filter by property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8" />
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Store className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium text-foreground">No stores found</p>
              <p className="text-muted-foreground">Get started by adding your first store</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Incharge</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStores.map((store) => (
                    <TableRow key={store.id} data-testid={`store-row-${store.id}`}>
                      <TableCell className="font-medium">{store.name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-muted rounded text-sm font-mono">
                          {store.code}
                        </span>
                      </TableCell>
                      <TableCell>{store.property_name || '-'}</TableCell>
                      <TableCell>{store.store_type || '-'}</TableCell>
                      <TableCell>
                        {store.incharge_name ? (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            {store.incharge_name}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(store.status)}`}>
                          {store.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(store)}
                            data-testid={`edit-store-${store.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedStore(store);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-store-${store.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[550px]">
          <DialogHeader>
            <DialogTitle>{selectedStore ? 'Edit Store' : 'Add Store'}</DialogTitle>
            <DialogDescription>
              {selectedStore ? 'Update store details' : 'Add a new store to your property'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Store Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="store-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Store Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    data-testid="store-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_id">Property *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                >
                  <SelectTrigger data-testid="store-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="store_type">Store Type</Label>
                  <Select
                    value={formData.store_type}
                    onValueChange={(value) => setFormData({ ...formData, store_type: value })}
                  >
                    <SelectTrigger data-testid="store-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {STORE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="store-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="incharge">Store Incharge</Label>
                <Select
                  value={formData.incharge_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, incharge_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="store-incharge-select">
                    <SelectValue placeholder="Select incharge" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Incharge Assigned</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          {user.full_name}
                          {user.role_name && <span className="text-xs text-muted-foreground">({user.role_name})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The incharge will be notified for stock acknowledgments
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  data-testid="store-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-store-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedStore ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Store</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedStore?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-store-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoresPage;
